/**
 * Generic CRM Object List Page component.
 * Orchestrates all list page functionality for any CRM object type.
 *
 * For pipelineable entities (deals, custom pipeline types), the default
 * view is the pipeline board.  An "All {entities}" header action links
 * to the table view.  Non-pipelineable entities render the table directly.
 */

import { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useOrg } from '@/contexts/OrgContext';
import type { HeaderAction } from '@/lib/base/page-layout/page-layout';
import { isMemoEntityType } from '@/lib/crm/record/memo/notes.utils';
import type { CrmObject } from '@/types/crm';

import type { ListPageConfig } from '../../core/types';
import type {
  CrmCreateClickHandler,
  CrmCreateDefaults,
} from '../../create/createOverrides';
import { getCrmCreateOverride } from '../../create/createOverrides';
import { ACTIVE_VIEW_PARAM } from '../../hooks/useActiveListView';
import { PipelinePage } from '../../pipeline';
import { MODE_PARAM } from '../urlListParams';
import { CRMObjectListPage as CRMObjectListPageBase } from './crm-object-list-page';

export interface CRMObjectListPageProps {
  /** List page configuration */
  config: ListPageConfig;
  /**
   * Service for creating objects. Omit for system-created entity types
   * (e.g., email threads) — the Add button is hidden when absent.
   */
  onCreateObject?: (
    orgId: number,
    data: Record<string, unknown>
  ) => Promise<void>;
  /**
   * Forwarded to the default create slideout's form as `onPropertyChange`.
   * Fires on every create-form field change. Used to react to field entry before
   * submit — e.g. the contacts page debounces this on `ahoy_email` to prefetch
   * the dedup lookup so it's off the submit path. Only applies to the table/base
   * render (non-pipeline entity types).
   */
  onCreatePropertyChange?: (propertyTypeName: string, value: unknown) => void;
  /**
   * Override what happens when the Add button is clicked. When provided, the
   * Add button is shown and this callback is invoked instead of the built-in
   * create slideout — use for entity types with custom create flows.
   *
   * The optional `defaults` arg lets callers (e.g. a pipeline-stage `+`
   * button) pre-fill values on the resulting modal. Plain `() => void`
   * callers stay compatible since the arg is optional.
   */
  onCreateClick?: CrmCreateClickHandler;
  /** Callback when viewing an object */
  onViewObject?: (object: CrmObject) => void;
  /** Whether to enable delete action (default: true) */
  enableDelete?: boolean;
  /** Content rendered before search/filter in toolbar */
  toolbarLeading?: React.ReactNode;
  /**
   * Extra header actions inserted before the Add button (and after
   * Import/Export when visible). Use for page-specific shortcuts like
   * "View Bookings" on the Booking Links page.
   */
  extraHeaderActions?: HeaderAction[];
  /** Override the count label in the subtitle (e.g., 'threads for Kevin') */
  countLabel?: string;
  /** Singular form of the count label (e.g., 'thread for Kevin') */
  countLabelSingular?: string;
}

/**
 * CRMObjectListPage is a generic list page component for CRM objects.
 * Renders pipeline view for pipelineable entities, table view for everything else.
 *
 * @example
 * ```tsx
 * <CRMObjectListPage
 *   config={contactListConfig}
 *   onCreateObject={async (orgId, data) => {
 *     await contactService.create(orgId, data);
 *   }}
 * />
 * ```
 */
/**
 * Resolve the display mode from a raw param value. The accepted tokens are a
 * closed set — keep in sync with LEGACY_MODE_TOKENS in useActiveListView, which
 * relies on them never colliding with a saved-view uuid.
 */
function resolveExplicitView(
  param: string | null
): 'table' | 'pipeline' | null {
  if (param === 'board' || param === 'pipeline') {
    return 'pipeline';
  }
  if (param === 'table') {
    return 'table';
  }
  return null;
}

export function CRMObjectListPage(props: CRMObjectListPageProps) {
  const { config } = props;
  const hasPipeline = !!config.stageAssociationTypeName;

  const [searchParams] = useSearchParams();
  // Display mode lives in `?mode=`. `?view=` carried it before `view` was
  // repurposed to mean WHICH view is selected, so a legacy link is still read
  // here — useActiveListView migrates the token to `?mode=` on its first write,
  // but that lands a navigation later, and this must resolve on first render.
  const explicitView =
    resolveExplicitView(searchParams.get(MODE_PARAM)) ??
    resolveExplicitView(searchParams.get(ACTIVE_VIEW_PARAM));
  const fallbackView: 'table' | 'pipeline' = hasPipeline ? 'pipeline' : 'table';
  const viewMode: 'table' | 'pipeline' =
    explicitView ?? config.defaultView ?? fallbackView;

  const pluralTitle = config.pluralTitle || `${config.title}s`;

  // Consult the registry for a custom create modal for this entity type.
  // When one exists:
  //   1. The Add-button click handler opens our override (defaults captured
  //      from the caller — e.g. the pipeline-stage `+` button passes the
  //      column's stageEntityId so the modal can pre-select that stage).
  //   2. We suppress `onCreateObject` downstream so PipelinePage's per-stage
  //      `+` flow takes the override branch instead of opening the default
  //      create form.
  //   3. The override component is rendered inline; its open/close state
  //      lives at this level so it survives whether we render the table view
  //      or the pipeline view below.
  // The override takes precedence over any caller-supplied `onCreateClick`
  // because the registry is the single source of truth for "which entity
  // types have custom create UX" (see lib/crm/create/createOverrides.tsx).
  const Override = getCrmCreateOverride(config.entityType);
  // Narrow the broad `config.entityType` to the memo family before handing it
  // to the override — its `entityTypeName` prop only accepts `MemoEntityType`.
  // `Override` is non-null only for memo types, so this is defined exactly when
  // `Override` is, and folding it into `hasOverride` lets the JSX below see it
  // as a non-undefined `MemoEntityType`.
  const overrideEntityType = isMemoEntityType(config.entityType)
    ? config.entityType
    : undefined;
  const { currentOrg } = useOrg();
  const overrideOrgId = currentOrg?.id;
  const [isOverrideOpen, setIsOverrideOpen] = useState(false);
  const [overrideDefaults, setOverrideDefaults] = useState<
    CrmCreateDefaults | undefined
  >(undefined);
  const handleOpenOverride = useCallback<CrmCreateClickHandler>(defaults => {
    setOverrideDefaults(defaults);
    setIsOverrideOpen(true);
  }, []);
  const handleCloseOverride = useCallback(() => {
    setIsOverrideOpen(false);
    // Drop defaults on close so the next open starts clean unless the caller
    // explicitly provides fresh defaults.
    setOverrideDefaults(undefined);
  }, []);

  const hasOverride = !!Override && !!overrideOrgId && !!overrideEntityType;

  // When the override exists, swap the Add-click handler. Otherwise pass
  // props.onCreateClick through unchanged.
  const effectiveOnCreateClick: CrmCreateClickHandler | undefined = hasOverride
    ? handleOpenOverride
    : props.onCreateClick;

  // Suppress onCreateObject whenever a registered override exists for this
  // entity type, regardless of whether `overrideOrgId` is populated yet.
  // PipelinePage prefers `onCreateObject` and would open its built-in
  // create form with the stageEntityId pre-filled, bypassing our override.
  // Gating this on `Override` alone (not `hasOverride`, which additionally
  // depends on `overrideOrgId`) prevents the default-create flow from
  // briefly opening during the `currentOrg` loading window. `overrideOrgId`
  // still gates `overrideElement` rendering so the modal itself only mounts
  // once we have a valid orgId to pass it.
  const effectiveOnCreateObject = Override ? undefined : props.onCreateObject;

  // Render the override modal alongside the list page so its open/close
  // state lives at this level — not inside the list page or pipeline page.
  // Skip rendering it entirely when orgId isn't ready yet; the Add button
  // is gated on the same check above.
  const overrideElement = hasOverride ? (
    <Override
      orgId={overrideOrgId}
      entityTypeName={overrideEntityType}
      isOpen={isOverrideOpen}
      onClose={handleCloseOverride}
      defaults={overrideDefaults}
    />
  ) : null;

  // Pipeline render is gated on having SOME way to create — either a
  // caller-supplied handler (`effectiveOnCreateObject` / `effectiveOnCreateClick`)
  // or a registered override. We accept `Override` (not `hasOverride`) here so
  // the gate doesn't briefly flip closed during the `currentOrg` loading
  // window: `effectiveOnCreateObject` is already suppressed whenever
  // `Override` exists (see above), so without including `Override` in this
  // OR-chain the pipeline view would unmount until `overrideOrgId` resolves.
  if (
    viewMode === 'pipeline' &&
    hasPipeline &&
    config.stageAssociationTypeName &&
    (effectiveOnCreateObject || effectiveOnCreateClick || Override)
  ) {
    return (
      <>
        <PipelinePage
          entityType={config.entityType}
          title={config.title}
          pluralTitle={pluralTitle}
          createProperties={config.createProperties || []}
          createAssociations={config.createAssociations || []}
          stageAssociationTypeName={config.stageAssociationTypeName}
          onCreateObject={effectiveOnCreateObject}
          onCreateClick={effectiveOnCreateClick}
          allItemsLabel='Table'
        />
        {overrideElement}
      </>
    );
  }

  return (
    <>
      <CRMObjectListPageBase
        config={config}
        onCreateObject={effectiveOnCreateObject}
        onCreatePropertyChange={props.onCreatePropertyChange}
        onCreateClick={effectiveOnCreateClick}
        onViewObject={props.onViewObject}
        enableDelete={props.enableDelete}
        toolbarLeading={props.toolbarLeading}
        extraHeaderActions={props.extraHeaderActions}
        countLabel={props.countLabel}
        countLabelSingular={props.countLabelSingular}
      />
      {overrideElement}
    </>
  );
}
